import React, { useEffect, useState } from "react";
import { Edit2, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";

import axios from "axios";
import toast from "react-hot-toast";

import NewsFormModal from "../../components/AdminSideComponent/News/Newsformmodal";
import DeleteConfirmModal from "../../components/AdminSideComponent/Common/Deleteconfirmmodal";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/news`;

const NewsList = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modalType, setModalType] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [expandedNews, setExpandedNews] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${API_BASE_URL}/getAll`);

      setNewsList(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNews = () => {
    setSelectedNews(null);
    setModalType("add");
  };

  const handleEditNews = (news) => {
    setSelectedNews(news);
    setModalType("edit");
  };

  const handleDeleteNews = (news) => {
    setSelectedNews(news);
    setModalType("delete");
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedNews(null);
  };

  const handleNewsSaved = () => {
    fetchNews();
    handleCloseModal();
    toast.success("News Saved Successfully");
  };

  const handleNewsDeleted = () => {
    fetchNews();
    handleCloseModal();
    toast.success("News Deleted Successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">News & Events</h1>

            <p className="text-gray-600 mt-2">Manage company news & events</p>
          </div>

          <button
            onClick={handleAddNews}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            <Plus size={20} />
            Add News
          </button>
        </div>

        {/* Error */}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Loading */}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : newsList.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No News Found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {newsList.map((news) => (
              <div
                key={news._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition border border-gray-200"
              >
                {/* Header */}

                <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                  <div className="flex gap-5 flex-1">
                    <img
                      src={news.cardImage}
                      alt={news.title}
                      className="w-32 h-24 rounded-lg object-cover border flex-shrink-0"
                    />

                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {news.title}
                      </h3>

                      <p className="text-gray-600 mt-2 line-clamp-2">
                        {news.shortDescription}
                      </p>

                      <div className="flex flex-wrap items-center gap-3 mt-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            news.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {news.isActive ? "Active" : "Inactive"}
                        </span>

                        <span className="text-sm text-gray-500">
                          Created :{" "}
                          {new Date(news.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditNews(news)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
                    >
                      <Edit2 size={20} />
                    </button>

                    <button
                      onClick={() => handleDeleteNews(news)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                    >
                      <Trash2 size={20} />
                    </button>

                    <button
                      onClick={() =>
                        setExpandedNews(
                          expandedNews === news._id ? null : news._id,
                        )
                      }
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                    >
                      {expandedNews === news._id ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded */}

                {expandedNews === news._id && (
                  <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 via-white to-gray-50 border-t border-gray-200">
                    <div className="rounded-xl overflow-hidden border shadow-sm">
                      <img
                        src={news.bannerImage}
                        alt={news.title}
                        className="w-full h-[320px] object-cover"
                      />
                    </div>

                    <div className="relative overflow-hidden rounded-xl border bg-white shadow-sm p-5">
                      <div className="absolute left-0 top-0 h-full w-1 bg-blue-500"></div>

                      <h4 className="text-blue-600 uppercase font-semibold text-sm">
                        Introduction
                      </h4>

                      <p className="mt-3 text-gray-700 leading-8 whitespace-pre-line">
                        {news.introText}
                      </p>
                    </div>

                    {news.sections?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-orange-600 uppercase font-semibold text-sm mb-5">
                          Sections
                        </h4>

                        <div className="space-y-5">
                          {news.sections.map((section, index) => (
                            <div
                              key={index}
                              className="rounded-xl border bg-gradient-to-r from-orange-50 via-white to-white p-5"
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                                  {index + 1}
                                </div>

                                <h3 className="text-lg font-bold">
                                  {section.heading}
                                </h3>
                              </div>

                              <p className="text-gray-600 whitespace-pre-line leading-8">
                                {section.content}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border bg-white shadow-sm p-5">
                      <h4 className="text-emerald-600 uppercase font-semibold text-sm mb-4">
                        Metadata
                      </h4>

                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-semibold">Created :</span>

                          <p className="text-gray-600 mt-1">
                            {new Date(news.createdAt).toLocaleString()}
                          </p>
                        </div>

                        <div>
                          <span className="font-semibold">Updated :</span>

                          <p className="text-gray-600 mt-1">
                            {new Date(news.updatedAt).toLocaleString()}
                          </p>
                        </div>

                        <div className="md:col-span-2">
                          <span className="font-semibold">News ID :</span>

                          <code className="ml-3 bg-gray-100 px-2 py-1 rounded text-xs break-all">
                            {news._id}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add / Edit Modal */}

      {(modalType === "add" || modalType === "edit") && (
        <NewsFormModal
          mode={modalType}
          news={selectedNews}
          onClose={handleCloseModal}
          onSave={handleNewsSaved}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {/* Delete Modal */}

      {modalType === "delete" && selectedNews && (
        <DeleteConfirmModal
          product={selectedNews}
          onClose={handleCloseModal}
          onDelete={handleNewsDeleted}
          apiBaseUrl={API_BASE_URL}
        />
      )}
    </div>
  );
};

export default NewsList;
